export interface Article {
  slug: string;
  title: string;
  summary: string;
  content: string;
  category: string;
  categoryName: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  tags: string[];
  featured: boolean;
}

export const articles: Article[] = [
  {
    slug: 'yu-wang-de-ben-zhi',
    title: '欲望的本质：我们为何渴望',
    summary: '欲望并非罪恶，而是人类最深层的生命力。本文从心理学和哲学视角剖析欲望的根源，帮助你理解那些无法言说的渴望背后的真相。',
    content: `<p>欲望，这个词语承载了太多的偏见与误解。在许多文化中，欲望往往被贴上负面的标签——贪婪、放纵、不道德。但真的是这样吗？</p>
    <h2>欲望的生物学基础</h2>
    <p>从生物学的角度来看，欲望是人类生存和繁衍的根本驱动力。多巴胺系统——我们大脑中的奖励回路——不仅驱动我们追求食物、安全和伴侣，也驱动我们追求意义、创造和连接。</p>
    <p>研究表明，当一个人面对自己真正渴望的事物时，大脑的腹侧被盖区（VTA）会释放多巴胺，产生一种强烈的驱动力。这不是弱点，而是一种精妙的生物设计。</p>
    <h2>欲望与自我认知</h2>
    <p>了解自己的欲望，就是了解自己的本质。我们常常以为知道自己想要什么，但实际上，我们的大部分欲望是被社会、家庭和文化塑造的。</p>
    <blockquote>真正的自我发现之旅，不在于寻找新的风景，而在于拥有新的眼睛去审视内心。</blockquote>
    <p>当我们剥去层层社会期待的外衣，直面内心最原始的渴望时，我们才能真正理解自己是谁。</p>
    <h2>欲望的转化力量</h2>
    <p>未被理解的欲望会成为内心的黑洞，吞噬我们的精力和注意力。而被理解、被接纳的欲望，则可以转化为惊人的创造力。</p>
    <p>历史上最伟大的艺术家、科学家和思想家，无一不是将自己的深层欲望转化为推动人类文明前进的力量。</p>
    <h2>实用建议</h2>
    <p>每天花十分钟，不做评判地记录下你内心浮现的渴望。无论它们看起来多么"不该有"，都诚实地面对它们。一个月后，你会发现一个更完整、更真实的自己。</p>`,
    category: 'shen-ceng-yu-wang',
    categoryName: '深层欲望',
    author: '林墨',
    date: '2026-08-10',
    readTime: '8分钟',
    image: 'https://readdy.ai/api/search-image?query=A%20single%20candle%20flame%20burning%20brightly%20in%20complete%20darkness%2C%20warm%20golden%20light%20illuminating%20surrounding%20space%2C%20minimalist%20composition%2C%20metaphorical%20representation%20of%20inner%20desire%2C%20fine%20art%20photography%20style%2C%20dramatic%20chiaroscuro%20lighting&width=800&height=600&seq=article-essence-01&orientation=landscape',
    tags: ['欲望心理学', '自我认知', '个人成长'],
    featured: true,
  },
  {
    slug: 'ai-qing-zhong-de-yu-wang-mi-ma',
    title: '爱情中的欲望密码',
    summary: '爱情中的吸引力从何而来？解密两性之间的欲望动力学，从神经科学到情感智慧，全方位解读亲密关系中的深层需求。',
    content: `<p>爱情，这个人类最古老也最神秘的话题，始终围绕着欲望展开。但我们对爱情中的欲望又了解多少呢？</p>
    <h2>吸引力的科学</h2>
    <p>当我们被某人吸引时，大脑中发生的变化堪比一场化学风暴。苯乙胺（PEA）让我们心跳加速，多巴胺让我们感到愉悦，去甲肾上腺素让我们注意力高度集中。</p>
    <p>但长久的吸引远远超越了化学层面。研究表明，长期伴侣之间的吸引力与"自我扩张理论"密切相关——我们被那些能帮助我们成为更好自己的人所吸引。</p>
    <h2>欲望的语言</h2>
    <p>每个人对爱的表达和接收方式都不同。有的人通过身体接触感受爱，有的人通过肯定的话语，还有的人通过共同经历。</p>
    <blockquote>真正的亲密，是两个人在彼此面前可以完全做自己，而不必担心被评判。</blockquote>
    <p>理解伴侣的"欲望语言"——那些让TA感到被渴望、被珍视的方式——是维持长期关系活力的关键。</p>
    <h2>欲望的节奏</h2>
    <p>爱情中的欲望有其自然的节奏——有高潮也有低谷。这不是问题，而是规律。关键在于学会与这个节奏共舞，而不是抗拒它。</p>`,
    category: 'ai-qing-yu-qin-mi',
    categoryName: '爱情与亲密',
    author: '苏晚',
    date: '2026-08-08',
    readTime: '10分钟',
    image: 'https://readdy.ai/api/search-image?query=Two%20hands%20almost%20touching%20with%20golden%20light%20between%20fingertips%2C%20dark%20background%2C%20intimate%20and%20romantic%20atmosphere%2C%20warm%20amber%20glow%2C%20minimalist%20fine%20art%20photography%2C%20sensual%20yet%20elegant%20composition&width=800&height=600&seq=article-love-01&orientation=landscape',
    tags: ['爱情', '亲密关系', '吸引力'],
    featured: true,
  },
  {
    slug: 'tu-po-zi-wo-xian-zhi',
    title: '突破自我限制：欲望作为引擎',
    summary: '你的内心深处藏着你从未意识到的力量。学会将欲望转化为前进的燃料，突破那些看不见的自我设限。',
    content: `<p>每个人心中都有一片未被探索的大陆。那里埋藏着我们最深的渴望，也隐藏着我们最大的潜能。</p>
    <h2>自我设限的根源</h2>
    <p>大多数限制并非来自外部，而是来自我们的内心。那些"我不行"、"这不适合我"、"太晚了"的声音，本质上是对未知的恐惧。</p>
    <p>心理学家称之为"自我效能感"——你对自己完成某件事的能力的信念。这个信念，直接决定了你能走多远。</p>
    <h2>欲望作为动力源</h2>
    <p>当你对某件事有着强烈的渴望时，你会发现自己拥有超乎想象的力量。这就是欲望的魔力——它能让你突破自己设定的边界。</p>
    <blockquote>真正的局限不是你能做什么，而是你敢渴望什么。</blockquote>
    <p>将注意力从"我做不到"转向"我渴望成为"，你会发现世界为你打开了新的大门。</p>
    <h2>实践路径</h2>
    <p>从今天开始，写下三个你一直想做但从未开始的事情。然后问自己：阻止我的是什么？是真正的障碍，还是只是我的想象？</p>`,
    category: 'zi-wo-shi-xian',
    categoryName: '自我实现',
    author: '陈望',
    date: '2026-08-05',
    readTime: '7分钟',
    image: 'https://readdy.ai/api/search-image?query=A%20figure%20breaking%20through%20glass%20surface%20from%20darkness%20into%20golden%20light%2C%20dramatic%20shattering%20effect%2C%20powerful%20motion%2C%20dark%20background%20with%20warm%20light%20burst%2C%20fine%20art%20conceptual%20photography%2C%20inspirational%20and%20dynamic&width=800&height=600&seq=article-break-01&orientation=landscape',
    tags: ['自我突破', '潜能开发', '欲望转化'],
    featured: true,
  },
  {
    slug: 'yin-cang-de-ke-wang',
    title: '隐藏的渴望：那些你不愿承认的欲望',
    summary: '我们每个人都有一些不愿面对甚至不愿承认的欲望。探索这些"暗面"，其实是通往完整的必经之路。',
    content: `<p>每个人都有秘密。不是那种会伤害别人的秘密，而是那些我们自己都不愿直视的、关于自己的真相。</p>
    <h2>阴影与光明</h2>
    <p>荣格心理学中有一个重要概念——"阴影自我"。那些被我们压抑、否认的部分，并不会消失，它们会在我们的梦境、情绪和行为中悄然出现。</p>
    <p>荣格说："与其做一个好人，不如做一个完整的人。"面对阴影，不是要放纵它，而是要理解它、整合它。</p>
    <h2>社会面具下的真实</h2>
    <p>我们每个人都戴着社会赋予的面具——好员工、好伴侣、好父母。但这些面具背后，是一个有血有肉、有各种需求的人。</p>
    <blockquote>敢于在安全的环境中直面自己的暗面，是成熟的标志。</blockquote>
    <p>在欲望之岛，我们提供一个安全、非评判的空间，让你可以直面那些平时难以启齿的渴望。</p>`,
    category: 'shen-ceng-yu-wang',
    categoryName: '深层欲望',
    author: '江月',
    date: '2026-08-03',
    readTime: '9分钟',
    image: 'https://readdy.ai/api/search-image?query=A%20mirror%20reflecting%20a%20different%20version%20of%20a%20person%20in%20dim%20light%2C%20dark%20mysterious%20atmosphere%2C%20warm%20amber%20accents%2C%20surreal%20and%20introspective%2C%20fine%20art%20conceptual%20photography%2C%20psychological%20depth%2C%20elegant%20composition&width=800&height=600&seq=article-hidden-01&orientation=landscape',
    tags: ['阴影自我', '荣格心理学', '自我接纳'],
    featured: false,
  },
  {
    slug: 'guan-xi-zhong-de-quan-li-you-xi',
    title: '关系中的权力游戏：谁在主导你的欲望',
    summary: '每一段关系都是一场微妙的权力博弈。看清关系中的权力动态，你才能真正掌握自己的情感主导权。',
    content: `<p>你有没有想过，为什么在某些关系中你总是感到被动？而在另一些关系中，你却意外地成为了主导者？</p>
    <h2>权力的无形流动</h2>
    <p>关系中的权力并非固定不变。它像水一样流动，随着时间、环境和双方心理状态的变化而改变。理解这种流动性，是维护健康关系的重要基础。</p>
    <h2>依赖与自主的平衡</h2>
    <p>健康的关系需要在依赖与自主之间找到平衡。过度依赖会让你失去自我，而过度独立则会让关系变得冰冷。</p>
    <blockquote>最好的关系是：我和你在一起，但我依然是我。</blockquote>
    <p>关键在于觉知——意识到自己在关系中的位置和需求，并有能力做出有意识的选择。</p>`,
    category: 'guan-xi-dong-li',
    categoryName: '关系动力',
    author: '林墨',
    date: '2026-07-28',
    readTime: '6分钟',
    image: 'https://readdy.ai/api/search-image?query=Two%20chess%20pieces%20facing%20each%20other%20on%20a%20dark%20board%2C%20golden%20light%20illuminating%20one%20piece%2C%20dramatic%20shadows%2C%20metaphorical%20power%20dynamics%2C%20fine%20art%20still%20life%20photography%2C%20warm%20amber%20tones&width=800&height=600&seq=article-power-01&orientation=landscape',
    tags: ['人际关系', '权力动态', '情感智慧'],
    featured: false,
  },
  {
    slug: 'qing-xu-de-di-tu',
    title: '情绪的地图：在混沌中找到方向',
    summary: '情绪不是敌人，而是指南针。学会阅读你的情绪地图，在看似混乱的感受中找到通往内心的路径。',
    content: `<p>你有没有过这样的时刻——内心百感交集，却说不清自己到底是什么感受？</p>
    <h2>情绪的层次</h2>
    <p>情绪不是单一的，而是一层又一层的。表层情绪（愤怒）之下，往往隐藏着更深的情感（受伤、恐惧）。学会逐层探索，你会发现情绪的真面目。</p>
    <h2>身体是情绪的容器</h2>
    <p>每一种情绪都会在身体上留下印记。焦虑可能表现为肩颈紧张，悲伤可能表现为胸口沉重。学会倾听身体的声音，是情绪智慧的第一步。</p>
    <blockquote>情绪不需要被控制，它们需要被理解。</blockquote>
    <p>当你不再与自己的情绪对抗，而是像对待朋友一样倾听它们时，你会发现自己获得了前所未有的内在平静。</p>`,
    category: 'qing-gan-tan-suo',
    categoryName: '情感探索',
    author: '苏晚',
    date: '2026-07-25',
    readTime: '8分钟',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20flowing%20color%20waves%20in%20darkness%2C%20amber%20and%20rose%20tones%20swirling%20together%2C%20emotional%20landscape%2C%20fluid%20art%20style%2C%20warm%20light%20breaking%20through%20dark%20background%2C%20fine%20art%20abstract%2C%20contemplative%20mood&width=800&height=600&seq=article-emotion-01&orientation=landscape',
    tags: ['情绪管理', '身体觉知', '情感智慧'],
    featured: false,
  },
  {
    slug: 'ming-xiang-yu-yu-wang',
    title: '冥想与欲望：静观内心的波澜',
    summary: '冥想不是压抑欲望，而是学会与欲望共处。通过正念练习，重新认识你的内在驱动力。',
    content: `<p>很多人以为冥想就是让大脑一片空白，什么也不想。这是一个巨大的误解。</p>
    <h2>观察而非压抑</h2>
    <p>真正的冥想不是压制念头和欲望，而是学会观察它们——像一个旁观者一样，看着念头升起、停留、消失。</p>
    <p>当你不再与欲望对抗，你会发现它们并没有想象中那么强大。真正消耗你能量的，往往是对欲望的抗拒本身。</p>
    <h2>欲望与空性</h2>
    <p>在东方哲学中，欲望被视为一种能量。这种能量本身没有好坏之分，关键在于我们如何引导它。</p>
    <blockquote>水能载舟，亦能覆舟。欲望也是如此。</blockquote>
    <p>通过定期的冥想练习，你会逐渐培养出一种内在的觉知力——在欲望升起时，你有了选择的自由，而不是被它裹挟。</p>`,
    category: 'shen-xin-he-yi',
    categoryName: '身心合一',
    author: '江月',
    date: '2026-07-20',
    readTime: '7分钟',
    image: 'https://readdy.ai/api/search-image?query=Silhouette%20of%20person%20meditating%20in%20lotus%20position%2C%20surrounded%20by%20soft%20golden%20light%20particles%2C%20dark%20peaceful%20background%2C%20spiritual%20atmosphere%2C%20warm%20amber%20glow%2C%20fine%20art%20photography%2C%20minimalist%20serene%20composition&width=800&height=600&seq=article-meditate-01&orientation=landscape',
    tags: ['冥想', '正念', '身心修行'],
    featured: false,
  },
  {
    slug: 'she-jiao-mei-ti-yu-yu-wang',
    title: '社交媒体与欲望：你在渴望什么',
    summary: '每一次滑动屏幕，都有欲望在驱动。社交媒体如何塑造我们的渴望？又该如何保持清醒？',
    content: `<p>每天打开手机，我们被无数精心设计的欲望触发器包围。点赞、关注、热门推荐——每一个按钮都在精准刺激我们的大脑奖励系统。</p>
    <h2>注意力经济的真相</h2>
    <p>社交媒体平台的核心商业模式是注意力经济。它们的设计目的就是让你停留更久、互动更多。而我们最原始的社会欲望——被认可、被关注、被爱——被巧妙地转化为了商业价值。</p>
    <h2>数字排毒的必要性</h2>
    <p>定期远离社交媒体，重新连接自己的内在感受，在当今时代比以往任何时候都更加重要。</p>
    <blockquote>当外在的声音太过嘈杂时，你就听不到自己内心的声音了。</blockquote>
    <p>试着每周安排一天的"数字安息日"——不查看任何社交媒体。你会惊讶地发现，真实的自己比线上的自己更丰富、更有趣。</p>`,
    category: 'guan-xi-dong-li',
    categoryName: '关系动力',
    author: '陈望',
    date: '2026-07-15',
    readTime: '6分钟',
    image: 'https://readdy.ai/api/search-image?query=Smartphone%20floating%20in%20darkness%20with%20golden%20light%20beams%20and%20digital%20particles%20surrounding%2C%20reflective%20screen%2C%20modern%20technology%20concept%2C%20dark%20atmospheric%20background%2C%20warm%20amber%20accents%2C%20fine%20art%20conceptual%20photography&width=800&height=600&seq=article-social-01&orientation=landscape',
    tags: ['社交媒体', '数字健康', '注意力'],
    featured: false,
  },
];

export const getFeaturedArticles = () => articles.filter((a) => a.featured);
export const getArticlesByCategory = (categorySlug: string) =>
  articles.filter((a) => a.category === categorySlug);
export const getArticleBySlug = (slug: string) =>
  articles.find((a) => a.slug === slug);
export const getRelatedArticles = (currentSlug: string, category: string, limit: number = 3) =>
  articles.filter((a) => a.category === category && a.slug !== currentSlug).slice(0, limit);